import { supabase } from "../lib/supabase";

/**
 * Sales metrics service.
 *
 * Replaces the previous implementation, which fired 9 single-worker RPCs per rep and
 * collapsed every failure into `0` via `.data || 0` - so a broken RPC was
 * indistinguishable from a genuine zero. Errors now propagate.
 *
 * Terminology: a "recorded door" is a row in `customers`. The schema has no knock or
 * visit table, so doors a rep approached without recording are not observable. Rates
 * are always "of recorded doors" and the UI must show the denominator.
 *
 * Rate fields are `number | null`. NULL means "no denominator" (no doors recorded in
 * the window), which is NOT the same as 0% - render it as "no data", never as zero.
 */

/** A rate expressed as a fraction in [0,1], or null when the denominator was zero. */
export type Rate = number | null;

export interface RepMetrics {
  workerId: number;
  fullName: string;
  recordedDoors: number;
  contacted: number;
  interested: number;
  /** Distinct customers who signed. The close-rate numerator. */
  closingCustomers: number;
  contractsSigned: number;
  contractsCancelled: number;
  contactRate: Rate;
  interestRate: Rate;
  /** Closing customers / recorded doors - same grain on both sides. */
  closeRate: Rate;
  cancelRate: Rate;
  /** Contracts per closing customer. 1.0 = no cross-selling. */
  contractsPerClose: number | null;
  revenue: number;
  avgContractValue: number;
  revenuePerDoor: number | null;
  revenueGoalMonth: number | null;
  goalAttainment: Rate;
  unpricedContracts: number;
}

export interface TeamMetrics {
  productLine: string;
  contractsSigned: number;
  contractsCancelled: number;
  revenue: number;
  avgContractValue: number;
  sellingReps: number;
  revenuePerRep: number | null;
  cancelRate: Rate;
  unpricedContracts: number;
}

export interface CompanyMetrics {
  recordedDoors: number;
  contacted: number;
  interested: number;
  closingCustomers: number;
  contractsSigned: number;
  contractsCancelled: number;
  contactRate: Rate;
  interestRate: Rate;
  closeRate: Rate;
  cancelRate: Rate;
  contractsPerClose: number | null;
  revenue: number;
  avgContractValue: number;
  revenuePerDoor: number | null;
  activeReps: number;
  forecastValue: number;
  unpricedContracts: number;
}

export interface TimeseriesPoint {
  bucket: string;
  /** Grain the server actually used - authoritative when "auto" was requested. */
  grain: Exclude<Grain, "auto">;
  recordedDoors: number;
  contractsSigned: number;
  revenue: number;
  closeRate: Rate;
}

/**
 * "auto" lets the server pick the finest grain that fits under PostgREST's row limit.
 * Prefer it: an explicit grain over a long range is refused (by design - the
 * alternative was silent truncation), so only pass one when the user chose it.
 */
export type Grain = "auto" | "day" | "week" | "month";

/**
 * Raw row shapes as returned by the RPCs (snake_case, Postgres numerics arriving as
 * strings). Declared explicitly rather than as an index signature so the mapping
 * below is checked against the actual SQL contract.
 */
type Numeric = number | string | null;

interface RepMetricsRow {
  worker_id: number;
  full_name: string | null;
  recorded_doors: Numeric;
  contacted: Numeric;
  interested: Numeric;
  closing_customers: Numeric;
  contracts_signed: Numeric;
  contracts_cancelled: Numeric;
  contact_rate: Numeric;
  interest_rate: Numeric;
  close_rate: Numeric;
  cancel_rate: Numeric;
  contracts_per_close: Numeric;
  revenue: Numeric;
  avg_contract_value: Numeric;
  revenue_per_door: Numeric;
  revenue_goal_month: Numeric;
  goal_attainment: Numeric;
  unpriced_contracts: Numeric;
}

interface TeamMetricsRow {
  product_line: string | null;
  contracts_signed: Numeric;
  contracts_cancelled: Numeric;
  revenue: Numeric;
  avg_contract_value: Numeric;
  selling_reps: Numeric;
  revenue_per_rep: Numeric;
  cancel_rate: Numeric;
  unpriced_contracts: Numeric;
}

interface CompanyMetricsRow {
  recorded_doors: Numeric;
  contacted: Numeric;
  interested: Numeric;
  closing_customers: Numeric;
  contracts_signed: Numeric;
  contracts_cancelled: Numeric;
  contact_rate: Numeric;
  interest_rate: Numeric;
  close_rate: Numeric;
  cancel_rate: Numeric;
  contracts_per_close: Numeric;
  revenue: Numeric;
  avg_contract_value: Numeric;
  revenue_per_door: Numeric;
  active_reps: Numeric;
  forecast_value: Numeric;
  unpriced_contracts: Numeric;
}

interface TimeseriesRow {
  bucket: string;
  grain: string;
  recorded_doors: Numeric;
  contracts_signed: Numeric;
  revenue: Numeric;
  close_rate: Numeric;
}

/**
 * Formats a Date as YYYY-MM-DD in the given IANA timezone.
 *
 * The previous implementation used `toISOString().split("T")[0]`, which converts to
 * UTC first - so for a Berlin user a date could shift back by one day near midnight,
 * silently querying the wrong window. The RPCs bucket `customers.created_at` at
 * Europe/Berlin, so the client must serialize in the same zone.
 */
export function toLocalDateString(
  date: Date,
  timeZone = "Europe/Berlin"
): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/** Coerces a Postgres numeric (which arrives as string or number) to a number. */
function num(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Same as `num`, but preserves null so "no denominator" stays distinguishable. */
function nullableNum(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export async function getRepMetrics(
  startDate: string,
  endDate: string
): Promise<RepMetrics[]> {
  const { data, error } = await supabase.rpc("get_rep_metrics", {
    p_start: startDate,
    p_end: endDate,
  });

  if (error) throw error;

  return ((data ?? []) as RepMetricsRow[]).map((row) => ({
    workerId: num(row.worker_id),
    fullName: String(row.full_name ?? "Unknown Worker"),
    recordedDoors: num(row.recorded_doors),
    contacted: num(row.contacted),
    interested: num(row.interested),
    closingCustomers: num(row.closing_customers),
    contractsSigned: num(row.contracts_signed),
    contractsCancelled: num(row.contracts_cancelled),
    contactRate: nullableNum(row.contact_rate),
    interestRate: nullableNum(row.interest_rate),
    closeRate: nullableNum(row.close_rate),
    cancelRate: nullableNum(row.cancel_rate),
    contractsPerClose: nullableNum(row.contracts_per_close),
    revenue: num(row.revenue),
    avgContractValue: num(row.avg_contract_value),
    revenuePerDoor: nullableNum(row.revenue_per_door),
    revenueGoalMonth: nullableNum(row.revenue_goal_month),
    goalAttainment: nullableNum(row.goal_attainment),
    unpricedContracts: num(row.unpriced_contracts),
  }));
}

export async function getTeamMetrics(
  startDate: string,
  endDate: string
): Promise<TeamMetrics[]> {
  const { data, error } = await supabase.rpc("get_team_metrics", {
    p_start: startDate,
    p_end: endDate,
  });

  if (error) throw error;

  return ((data ?? []) as TeamMetricsRow[]).map((row) => ({
    productLine: String(row.product_line ?? "Other"),
    contractsSigned: num(row.contracts_signed),
    contractsCancelled: num(row.contracts_cancelled),
    revenue: num(row.revenue),
    avgContractValue: num(row.avg_contract_value),
    sellingReps: num(row.selling_reps),
    revenuePerRep: nullableNum(row.revenue_per_rep),
    cancelRate: nullableNum(row.cancel_rate),
    unpricedContracts: num(row.unpriced_contracts),
  }));
}

export async function getCompanyMetrics(
  startDate: string,
  endDate: string
): Promise<CompanyMetrics | null> {
  const { data, error } = await supabase.rpc("get_company_metrics", {
    p_start: startDate,
    p_end: endDate,
  });

  if (error) throw error;

  const row = ((data ?? []) as CompanyMetricsRow[])[0];
  if (!row) return null;

  return {
    recordedDoors: num(row.recorded_doors),
    contacted: num(row.contacted),
    interested: num(row.interested),
    closingCustomers: num(row.closing_customers),
    contractsSigned: num(row.contracts_signed),
    contractsCancelled: num(row.contracts_cancelled),
    contactRate: nullableNum(row.contact_rate),
    interestRate: nullableNum(row.interest_rate),
    closeRate: nullableNum(row.close_rate),
    cancelRate: nullableNum(row.cancel_rate),
    contractsPerClose: nullableNum(row.contracts_per_close),
    revenue: num(row.revenue),
    avgContractValue: num(row.avg_contract_value),
    revenuePerDoor: nullableNum(row.revenue_per_door),
    activeReps: num(row.active_reps),
    forecastValue: num(row.forecast_value),
    unpricedContracts: num(row.unpriced_contracts),
  };
}

export async function getMetricsTimeseries(
  startDate: string,
  endDate: string,
  grain: Grain = "auto"
): Promise<TimeseriesPoint[]> {
  const { data, error } = await supabase.rpc("get_metrics_timeseries", {
    p_start: startDate,
    p_end: endDate,
    p_grain: grain,
  });

  if (error) throw error;

  return ((data ?? []) as TimeseriesRow[]).map((row) => ({
    bucket: String(row.bucket),
    grain: (row.grain === "week" || row.grain === "month"
      ? row.grain
      : "day") as Exclude<Grain, "auto">,
    recordedDoors: num(row.recorded_doors),
    contractsSigned: num(row.contracts_signed),
    revenue: num(row.revenue),
    closeRate: nullableNum(row.close_rate),
  }));
}

/**
 * Turns a rejection into a message a human can act on.
 *
 * Supabase rejects with a PostgrestError - a plain object, not an Error - so a naive
 * String(err) yields "[object Object]" and hides the actual problem. The most common
 * real cause is PGRST202 (function not found), which means the migrations have not been
 * applied to whichever database this client points at; local Docker and the remote
 * project are easy to mix up, so that case gets an explicit hint.
 */
function describeError(reason: unknown): string {
  if (reason instanceof Error) return reason.message;

  if (reason && typeof reason === "object") {
    const err = reason as {
      message?: string;
      code?: string;
      details?: string;
      hint?: string;
    };
    const parts = [err.message, err.details, err.hint].filter(Boolean);
    const base = parts.length > 0 ? parts.join(" · ") : JSON.stringify(reason);

    if (err.code === "PGRST202") {
      return `${base} — die Metrik-Funktionen fehlen in dieser Datenbank. Migrationen anwenden (supabase db push bzw. psql -f supabase/migrations/…).`;
    }
    return err.code ? `[${err.code}] ${base}` : base;
  }

  return String(reason ?? "unbekannter Fehler");
}

export interface AllMetrics {
  company: CompanyMetrics | null;
  teams: TeamMetrics[];
  reps: RepMetrics[];
  timeseries: TimeseriesPoint[];
}

/**
 * Fetches every scope in one pass - 4 round-trips total, versus 9 per rep before.
 *
 * Uses allSettled so one failing scope does not blank the whole page; failures are
 * rethrown as a single aggregate error naming the scopes that failed, so the UI can
 * report what is actually broken instead of rendering zeros.
 */
export async function getAllMetrics(
  startDate: string,
  endDate: string,
  grain: Grain = "auto"
): Promise<AllMetrics> {
  const [company, teams, reps, timeseries] = await Promise.allSettled([
    getCompanyMetrics(startDate, endDate),
    getTeamMetrics(startDate, endDate),
    getRepMetrics(startDate, endDate),
    getMetricsTimeseries(startDate, endDate, grain),
  ]);

  const failures: string[] = [];
  if (company.status === "rejected") failures.push("company");
  if (teams.status === "rejected") failures.push("teams");
  if (reps.status === "rejected") failures.push("reps");
  if (timeseries.status === "rejected") failures.push("trend");

  if (failures.length > 0) {
    const first = [company, teams, reps, timeseries].find(
      (r): r is PromiseRejectedResult => r.status === "rejected"
    );
    throw new Error(
      `Metriken konnten nicht geladen werden (${failures.join(", ")}): ${describeError(first?.reason)}`
    );
  }

  return {
    company: (company as PromiseFulfilledResult<CompanyMetrics | null>).value,
    teams: (teams as PromiseFulfilledResult<TeamMetrics[]>).value,
    reps: (reps as PromiseFulfilledResult<RepMetrics[]>).value,
    timeseries: (timeseries as PromiseFulfilledResult<TimeseriesPoint[]>).value,
  };
}
